import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import {
  ArrowLeft,
  ArrowRight,
  Monitor,
  Shield,
  Lock,
  Ban,
  Eye,
  Users,
  Zap,
  Settings,
  ShieldCheck,
  RefreshCw,
  Smartphone,
  Package,
  CheckCircle2,
} from "lucide-react";
import { motion } from "framer-motion";
import kioskHeroBg from "@/assets/kiosk-hero-bg.jpg";

const highlights = [
  {
    icon: Shield,
    title: "Maximale veiligheid",
    text: "Gebruikers zien alleen de apps die ze nodig hebben. Geen toegang tot instellingen, geen software installeren, geen onverwacht gedrag. Dit vermindert de kans op datalekken, malware en menselijke fouten drastisch.",
    color: "bg-hero-gradient",
  },
  {
    icon: Users,
    title: "Perfect voor gedeelde apparaten",
    text: "Het apparaat blijft altijd in dezelfde gecontroleerde staat — ongeacht wie erachter zit. Ideaal voor roulerend personeel op de winkelvloer, in het magazijn, de zorg of bij de receptie.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: Zap,
    title: "Lagere IT-beheerlast",
    text: "Minder supporttickets, minder troubleshooting, apparaten raken niet 'vervuild'. Eén druk op de knop via Intune en het apparaat staat weer in kiosk-modus. Altijd schoon en foutloos.",
    color: "bg-rinqel-green-gradient",
  },
  {
    icon: CheckCircle2,
    title: "Extreem simpel in gebruik",
    text: "Geen startmenu, geen bureaublad, geen opties om te verdwalen. Medewerkers zien alleen wat ze moeten gebruiken — en niets meer. Snellere onboarding, minder training nodig.",
    color: "bg-hero-gradient",
  },
  {
    icon: RefreshCw,
    title: "Altijd consistent",
    text: "Elke gebruiker start in exact dezelfde omgeving, elke dag opnieuw. Uniforme werkwijze, minder fouten en snellere onboarding van nieuw personeel.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: Settings,
    title: "Volledig beheerd door Rinqel",
    text: "Wij configureren de kiosk-modus, beheren updates via Intune en zorgen dat alles veilig en up-to-date blijft. Jij hoeft je nergens druk om te maken.",
    color: "bg-rinqel-green-gradient",
  },
];

const useCases = [
  {
    icon: Package,
    title: "Winkelvloer & retail",
    text: "Medewerkers zien alleen de kassa-app of tijdregistratie. Geen afleiding, geen risico dat iemand instellingen wijzigt.",
  },
  {
    icon: Users,
    title: "Zorg (gedeelde apparaten)",
    text: "Verpleegkundigen gebruiken één gedeeld apparaat met zorgapplicaties, zonder risico op verkeerde inlog of ongewenste toegang.",
  },
  {
    icon: Monitor,
    title: "Spreekkamers & receptie",
    text: "Bezoekers of medewerkers kunnen een pc gebruiken zonder toegang tot bedrijfsnetwerk of bestanden.",
  },
  {
    icon: Smartphone,
    title: "Logistiek & magazijn",
    text: "Apparaten voor orderpicking, voorraadbeheer of scanning draaien alleen de benodigde apps.",
  },
];

const WerkplekKiosk = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[420px] overflow-hidden">
        <div className="absolute inset-0">
          <img loading="eager" decoding="async" fetchPriority="high" src={kioskHeroBg} alt="Kiosk werkplek: veilige gedeelde apparaten voor ploegendiensten" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(210,25%,12%)]/90 via-[hsl(210,30%,18%)]/80 to-[hsl(204,60%,30%)]/70" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.1),transparent_60%)]" />
        <div className="container relative z-10 flex min-h-[420px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/werkplek"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Digitale Werkplek
            </Link>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Beheerd via Microsoft Intune
              </span>
            </motion.div>

            <motion.div
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <Monitor className="h-7 w-7 text-white" />
            </motion.div>

            <motion.h1
              className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Managed Kiosk{" "}
              <span className="text-gradient">Werkplek</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Een vergrendelde, veilige werkplek voor gedeelde apparaten. Alleen de apps die nodig zijn — niets meer. Ideaal voor retail, zorg, logistiek en receptie.
            </motion.p>

            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="text-4xl font-bold text-white">€25</span>
              <span className="ml-1 text-white/70">/per maand per kiosk-account</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Single-app vs Multi-app */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Single-app of multi-app: <span className="text-gradient">u kiest</span>
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Afhankelijk van uw situatie vergrendelen wij het apparaat op één applicatie of een kleine, gecontroleerde set apps.
            </p>
          </motion.div>

          <div className="mx-auto mt-10 grid max-w-4xl gap-6 md:grid-cols-2">
            <motion.div
              className="rounded-xl border border-border bg-card p-6 shadow-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-hero-gradient text-primary-foreground mb-4">
                <Lock className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold">Single-app kiosk</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Volledig vergrendeld op één toepassing — maximaal veilig en eenvoudig.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Kassasystemen</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Zelfbedieningszuilen</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Bezoekersregistratie</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Tijdschrijf- of orderregistratie</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Magazijnscanners</li>
              </ul>
            </motion.div>

            <motion.div
              className="rounded-xl border border-border bg-card p-6 shadow-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-rinqel-blue-gradient text-primary-foreground mb-4">
                <Ban className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold">Multi-app kiosk</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Een kleine, gecontroleerde set applicaties — veilig en overzichtelijk.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Logistiek (scannerapp + Teams + orderapp)</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Zorg (EPD + communicatie-app)</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Winkelvloer (kassa + rooster + chat)</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Publieke pc's met beperkte functies</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Voordelen */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.h2
            className="mb-12 text-center text-2xl font-bold md:text-3xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Waarom een kiosk-werkplek{" "}
            <span className="text-gradient">ideaal is</span>
          </motion.h2>

          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2">
            {highlights.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${item.color} text-primary-foreground`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ideaal voor */}
      <section className="py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Ideaal voor <span className="text-gradient">deze omgevingen</span>
            </h2>
          </motion.div>

          <div className="mx-auto mt-10 grid max-w-4xl gap-5 md:grid-cols-2">
            {useCases.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="flex gap-4 rounded-xl border border-border bg-card p-6 shadow-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Concrete voordelen tabel */}
      <section className="bg-muted/40 py-16 md:py-20">
        <div className="container">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Wat levert dit uw organisatie{" "}
              <span className="text-gradient">concreet op?</span>
            </h2>
          </motion.div>

          <div className="mx-auto mt-10 grid max-w-4xl gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[
              { label: "Minder risico's", desc: "Strikte afscherming voorkomt datalekken, fouten en misbruik.", gradient: "bg-hero-gradient" },
              { label: "Betere beveiliging", desc: "Het apparaat doet alleen wat het moet doen — geen toegang tot de rest van Windows.", gradient: "bg-rinqel-blue-gradient" },
              { label: "Lagere kosten", desc: "Minder beheer, minder supporttickets, minder downtime.", gradient: "bg-rinqel-green-gradient" },
              { label: "Betere focus & productiviteit", desc: "Medewerkers werken sneller zonder afleiding.", gradient: "bg-hero-gradient" },
              { label: "Ideaal voor roulerend personeel", desc: "Elke gebruiker werkt in dezelfde, veilige omgeving.", gradient: "bg-rinqel-blue-gradient" },
              { label: "Perfect voor gedeelde plekken", desc: "Apparaten blijven altijd schoon en betrouwbaar.", gradient: "bg-rinqel-green-gradient" },
            ].map((row, i) => (
              <motion.div
                key={row.label}
                className="rounded-xl border border-border bg-card shadow-card overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
              >
                <div className={`${row.gradient} px-5 py-3`}>
                  <span className="text-sm font-bold text-white">{row.label}</span>
                </div>
                <div className="px-5 py-4">
                  <p className="text-sm leading-relaxed text-muted-foreground">{row.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12">
        <div className="container">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center">
            <Button variant="hero" size="lg" asChild>
              <TrackedLink to="/contact" trackLabel="Vraag aan" trackLocation="werkplek-kiosk-cta">
                Vraag aan
                <ArrowRight className="ml-1 h-4 w-4" />
              </TrackedLink>
            </Button>
            <Button variant="hero-outline" size="lg" asChild>
              <Link to="/diensten/werkplek">Terug naar overzicht</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default WerkplekKiosk;
